import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { AnimationType } from './AnimatedText';

interface TranslatedTextProps {
  /** Translation key from i18n */
  i18nKey: string;
  /** Optional values to interpolate in the translation */
  values?: Record<string, unknown>;
  /** Animation type */
  type?: AnimationType;
  /** Duration of the animation */
  duration?: number;
  /** Delay before animation starts */
  delay?: number;
  /** Stagger between elements (for word/letter animations) */
  stagger?: number;
  /** Additional CSS classes */
  className?: string;
}

// Digital characters for scramble effect
const digitalChars = '01アイウエオカキクケコサシスセソタチツテト';

// Animation variants for different types
const getVariants = (type: AnimationType) => {
  switch (type) {
    case 'fade':
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      };
    case 'slide':
      return {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -20 },
      };
    case 'blur':
      return {
        initial: { opacity: 0, filter: 'blur(10px)' },
        animate: { opacity: 1, filter: 'blur(0px)' },
        exit: { opacity: 0, filter: 'blur(10px)' },
      };
    default:
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      };
  }
};

// Digital scramble text component with enter/exit
function DigitalTransitionText({ 
  text, 
  duration = 0.8,
  className = '',
}: { 
  text: string; 
  duration?: number;
  className?: string;
}) {
  const [displayText, setDisplayText] = useState('');
  const [_phase, setPhase] = useState<'entering' | 'complete'>('entering');

  useEffect(() => {
    setPhase('entering');
    setDisplayText('');
    
    let iteration = 0;
    const totalIterations = text.length * 3;
    
    const intervalId = setInterval(() => {
      setDisplayText(
        text.split('').map((char, index) => {
          if (char === ' ') return ' ';
          if (index < iteration / 3) return char;
          return digitalChars[Math.floor(Math.random() * digitalChars.length)];
        }).join('')
      );
      
      iteration++;
      
      if (iteration >= totalIterations) {
        clearInterval(intervalId);
        setDisplayText(text);
        setPhase('complete');
      }
    }, (duration * 1000) / totalIterations);

    return () => clearInterval(intervalId);
  }, [text, duration]);

  return (
    <motion.span
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, filter: 'blur(4px)' }}
      transition={{ duration: 0.15 }}
    >
      {displayText || text.split('').map(() => '\u00A0').join('')}
    </motion.span>
  );
}

// Fade words with enter/exit
function FadeWordsTransition({ 
  text, 
  duration = 0.3,
  stagger = 0.05,
  className = '',
  isExiting: _isExiting = false,
}: { 
  text: string; 
  duration?: number;
  stagger?: number;
  className?: string;
  isExiting?: boolean;
}) {
  const words = text.split(' ');

  return (
    <motion.span
      className={className}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {words.map((word, i) => (
        <motion.span
          key={`${word}-${i}`}
          variants={{
            initial: { opacity: 0, y: 10 },
            animate: { 
              opacity: 1, 
              y: 0,
              transition: { duration, delay: i * stagger, ease: 'easeOut' }
            },
            exit: { 
              opacity: 0, 
              y: -10,
              transition: { duration: duration * 0.7, delay: (words.length - i - 1) * (stagger * 0.5) }
            },
          }}
          style={{ display: 'inline-block', marginRight: '0.25em' }}
        >
          {word}
        </motion.span>
      ))}
    </motion.span>
  );
}

// Fade letters with enter/exit
function FadeLettersTransition({ 
  text, 
  duration = 0.2,
  stagger = 0.015,
  className = '',
}: { 
  text: string; 
  duration?: number;
  stagger?: number;
  className?: string;
}) {
  const characters = text.split('');

  return (
    <motion.span
      className={className}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {characters.map((char, i) => (
        <motion.span
          key={`${char}-${i}`}
          variants={{
            initial: { opacity: 0, scale: 0.8, y: 5 },
            animate: { 
              opacity: 1, 
              scale: 1,
              y: 0,
              transition: { duration, delay: i * stagger, ease: 'easeOut' }
            },
            exit: { 
              opacity: 0, 
              scale: 0.8,
              y: -5,
              transition: { duration: duration * 0.6, delay: (characters.length - i - 1) * (stagger * 0.3) }
            },
          }}
          style={{ display: 'inline-block' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </motion.span>
  );
}

// Slide words with enter/exit
function SlideWordsTransition({ 
  text, 
  duration = 0.4,
  stagger = 0.08,
  className = '',
}: { 
  text: string; 
  duration?: number;
  stagger?: number;
  className?: string;
}) {
  const words = text.split(' ');

  return (
    <motion.span
      className={className}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{ perspective: '500px' }}
    >
      {words.map((word, i) => (
        <motion.span
          key={`${word}-${i}`}
          variants={{
            initial: { opacity: 0, y: 25, rotateX: -60 },
            animate: { 
              opacity: 1, 
              y: 0,
              rotateX: 0,
              transition: { duration, delay: i * stagger, ease: [0.25, 0.46, 0.45, 0.94] }
            },
            exit: { 
              opacity: 0, 
              y: -25,
              rotateX: 60,
              transition: { duration: duration * 0.6, delay: (words.length - i - 1) * (stagger * 0.4) }
            },
          }}
          style={{ 
            display: 'inline-block', 
            marginRight: '0.25em',
            transformOrigin: 'center bottom',
          }}
        >
          {word}
        </motion.span>
      ))}
    </motion.span>
  );
}

// Wave text with enter/exit
function WaveTransition({ 
  text, 
  duration = 0.3,
  stagger = 0.03,
  className = '',
}: { 
  text: string; 
  duration?: number;
  stagger?: number;
  className?: string;
}) {
  const characters = text.split('');

  return (
    <motion.span
      className={className}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {characters.map((char, i) => (
        <motion.span
          key={`${char}-${i}`}
          variants={{
            initial: { opacity: 0, y: 20 },
            animate: { 
              opacity: 1, 
              y: 0,
              transition: { duration, delay: i * stagger, ease: [0.25, 0.46, 0.45, 0.94] }
            },
            exit: { 
              opacity: 0, 
              y: -20,
              transition: { duration: duration * 0.5, delay: (characters.length - i - 1) * (stagger * 0.3) }
            },
          }}
          style={{ display: 'inline-block' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </motion.span>
  );
}

// Simple animated text with enter/exit
function SimpleTransition({ 
  text, 
  type,
  duration = 0.4,
  className = '',
}: { 
  text: string; 
  type: AnimationType;
  duration?: number;
  className?: string;
}) {
  const variants = getVariants(type);

  return (
    <motion.span
      className={className}
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
      transition={{ duration, ease: 'easeInOut' }}
    >
      {text}
    </motion.span>
  );
}

/**
 * Component that renders translated text with animated transitions
 * when the language changes - with both enter and exit animations.
 */
export function TranslatedText({
  i18nKey,
  values,
  type = 'digital',
  duration = 0.8,
  delay: _delay = 0,
  stagger = 0.05,
  className = '',
}: TranslatedTextProps) {
  const { t, i18n } = useTranslation();
  const [textKey, setTextKey] = useState(0);
  const currentText = t(i18nKey, values as Record<string, string>);
  const prevTextRef = useRef(currentText);

  // Listen for language changes and trigger re-animation
  useEffect(() => {
    const handleLanguageChanged = () => {
      setTextKey(prev => prev + 1);
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  // Also trigger when text content changes
  useEffect(() => {
    if (currentText !== prevTextRef.current) {
      setTextKey(prev => prev + 1);
      prevTextRef.current = currentText;
    }
  }, [currentText]);

  const renderContent = () => {
    switch (type) {
      case 'digital':
      case 'scramble':
        return (
          <DigitalTransitionText 
            text={currentText} 
            duration={duration}
            className={className}
          />
        );
      case 'fadeWords':
        return (
          <FadeWordsTransition 
            text={currentText} 
            duration={duration}
            stagger={stagger}
            className={className}
          />
        );
      case 'fadeLetters':
        return (
          <FadeLettersTransition 
            text={currentText} 
            duration={duration}
            stagger={stagger}
            className={className}
          />
        );
      case 'slideWords':
        return (
          <SlideWordsTransition 
            text={currentText} 
            duration={duration}
            stagger={stagger}
            className={className}
          />
        );
      case 'wave':
        return (
          <WaveTransition 
            text={currentText} 
            duration={duration}
            stagger={stagger}
            className={className}
          />
        );
      case 'fade':
      case 'slide':
      case 'blur':
      default:
        return (
          <SimpleTransition 
            text={currentText}
            type={type}
            duration={duration}
            className={className}
          />
        );
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.span key={`${i18nKey}-${textKey}`} style={{ display: 'inline' }}>
        {renderContent()}
      </motion.span>
    </AnimatePresence>
  );
}

/**
 * Pre-configured components for common animation types
 */

export function DigitalTranslatedText(props: Omit<TranslatedTextProps, 'type'>) {
  return <TranslatedText {...props} type="digital" />;
}

export function ScrambleTranslatedText(props: Omit<TranslatedTextProps, 'type'>) {
  return <TranslatedText {...props} type="scramble" />;
}

export function WaveTranslatedText(props: Omit<TranslatedTextProps, 'type'>) {
  return <TranslatedText {...props} type="wave" />;
}

export function FadeTranslatedText(props: Omit<TranslatedTextProps, 'type'>) {
  return <TranslatedText {...props} type="fade" />;
}

export function SlideTranslatedText(props: Omit<TranslatedTextProps, 'type'>) {
  return <TranslatedText {...props} type="slide" />;
}

export function BlurTranslatedText(props: Omit<TranslatedTextProps, 'type'>) {
  return <TranslatedText {...props} type="blur" />;
}

export function FadeWordsTranslatedText(props: Omit<TranslatedTextProps, 'type'>) {
  return <TranslatedText {...props} type="fadeWords" />;
}

export function FadeLettersTranslatedText(props: Omit<TranslatedTextProps, 'type'>) {
  return <TranslatedText {...props} type="fadeLetters" />;
}

export function SlideWordsTranslatedText(props: Omit<TranslatedTextProps, 'type'>) {
  return <TranslatedText {...props} type="slideWords" />;
}

export default TranslatedText;
